import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  Package,
  Save,
  RefreshCw,
  Filter,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Search,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const StockLevelBulkEditor = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [summary, setSummary] = useState(null);
  const [editedProducts, setEditedProducts] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const LIMIT = 100;

  // Fetch summary stats
  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/products/stock-level-summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  }, []);

  // Fetch at-risk products
  const fetchProducts = useCallback(async (filter = filterType, skipVal = 0, append = false) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/products/at-risk-stock?filter_type=${filter}&limit=${LIMIT}&skip=${skipVal}`
      );
      
      const { products: newProducts, total, has_more } = response.data;
      
      if (append) {
        setProducts(prev => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
        setEditedProducts({}); // Clear edits when filter changes
      }
      
      setHasMore(has_more);
      setSkip(skipVal);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchSummary();
    fetchProducts("all", 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (value) => {
    setFilterType(value);
    setSkip(0);
    fetchProducts(value, 0);
  };

  const loadMore = () => {
    const newSkip = skip + LIMIT;
    fetchProducts(filterType, newSkip, true);
  };

  const handleFieldChange = (productId, field, value) => {
    const numValue = value === "" ? null : parseFloat(value);
    
    setEditedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: numValue
      }
    }));
  };

  const getEditedValue = (productId, field, originalValue) => {
    if (editedProducts[productId] && editedProducts[productId][field] !== undefined) {
      return editedProducts[productId][field];
    }
    return originalValue;
  };

  const isEdited = (productId) => {
    return editedProducts[productId] && Object.keys(editedProducts[productId]).length > 0;
  };

  const handleSaveAll = async () => {
    const updates = Object.entries(editedProducts).map(([productId, changes]) => ({
      product_id: productId,
      ...changes
    }));

    if (updates.length === 0) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/api/products/bulk-update-stock-levels`, updates);
      toast.success(`Updated ${response.data.updated} products successfully`);
      setEditedProducts({});
      fetchProducts(filterType, 0);
      fetchSummary();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setEditedProducts({});
    toast.info("Changes discarded");
  };

  const getRiskBadge = (product) => {
    const reasons = product.risk_reasons || [];
    const primary = product.primary_risk || reasons[0];
    
    if (primary === 'below_minimum') {
      return <Badge variant="destructive" className="text-xs">Below Min</Badge>;
    }
    if (primary === 'at_reorder') {
      return <Badge className="bg-orange-500 text-xs">At Reorder</Badge>;
    }
    if (primary === 'no_levels_set') {
      return <Badge className="bg-yellow-500 text-xs">No Levels</Badge>;
    }
    if (primary === 'zero_stock_with_demand') {
      return <Badge variant="destructive" className="text-xs">Zero Stock</Badge>;
    }
    return <Badge variant="secondary" className="text-xs">At Risk</Badge>;
  };

  const filteredProducts = searchQuery
    ? products.filter(p => 
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.plu_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.barcode?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : products;

  const editedCount = Object.keys(editedProducts).length;

  return (
    <div className="p-6 space-y-6" data-testid="stock-level-bulk-editor">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/stock-master">
            <Button variant="ghost" size="sm" data-testid="back-to-stock-master">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Stock Master
            </Button>
          </Link>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Stock Level Bulk Editor
          </h1>
        </div>
        <div className="flex gap-2">
          {editedCount > 0 && (
            <>
              <Button 
                variant="outline" 
                onClick={handleDiscardChanges}
                data-testid="discard-changes-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Discard ({editedCount})
              </Button>
              <Button 
                onClick={handleSaveAll}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-all-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save All ({editedCount})
              </Button>
            </>
          )}
          <Button 
            variant="outline"
            onClick={() => {
              fetchProducts(filterType, 0);
              fetchSummary();
            }}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{summary.total_at_risk}</p>
              <p className="text-xs text-red-700">Total At Risk</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{summary.no_levels_set}</p>
              <p className="text-xs text-yellow-700">No Levels Set</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{summary.below_minimum}</p>
              <p className="text-xs text-orange-700">Below Min</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{summary.at_reorder}</p>
              <p className="text-xs text-amber-700">At Reorder</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-600">{summary.zero_stock}</p>
              <p className="text-xs text-gray-700">Zero Stock</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{summary.healthy}</p>
              <p className="text-xs text-green-700">Healthy</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{summary.total_products}</p>
              <p className="text-xs text-blue-700">Total Products</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <Select value={filterType} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-[200px]" data-testid="filter-select">
              <SelectValue placeholder="Filter by risk" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All At Risk</SelectItem>
              <SelectItem value="no_levels">No Levels Set</SelectItem>
              <SelectItem value="below_min">Below Minimum</SelectItem>
              <SelectItem value="zero_stock">Zero Stock</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name, PLU, or barcode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        <p className="text-sm text-gray-600">
          Showing {filteredProducts.length} products
          {editedCount > 0 && (
            <span className="ml-2 text-orange-600 font-medium">
              ({editedCount} edited)
            </span>
          )}
        </p>
      </div>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[80px]">PLU</TableHead>
                  <TableHead className="min-w-[200px]">Product Name</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[100px] text-right">Current Stock</TableHead>
                  <TableHead className="w-[120px] text-center bg-blue-50">Minimum</TableHead>
                  <TableHead className="w-[120px] text-center bg-yellow-50">Reorder Qty</TableHead>
                  <TableHead className="w-[120px] text-center bg-green-50">Maximum</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                      <p className="mt-2 text-gray-500">Loading products...</p>
                    </TableCell>
                  </TableRow>
                ) : filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                      <p className="text-gray-600 font-medium">All products are healthy!</p>
                      <p className="text-gray-400 text-sm">No products match the selected filter.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => (
                    <TableRow 
                      key={product.id} 
                      className={isEdited(product.id) ? "bg-orange-50" : ""}
                      data-testid={`product-row-${product.id}`}
                    >
                      <TableCell className="font-mono text-sm">
                        {product.plu_code || "-"}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          {product.barcode && (
                            <p className="text-xs text-gray-400">{product.barcode}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getRiskBadge(product)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        <span className={
                          (product.current_stock || 0) <= 0 
                            ? "text-red-600" 
                            : (product.current_stock || 0) < (product.minimum_level || 0)
                              ? "text-orange-600"
                              : "text-gray-900"
                        }>
                          {(product.current_stock || 0).toFixed(0)}
                        </span>
                      </TableCell>
                      <TableCell className="bg-blue-50/50">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          className="w-full text-center h-8"
                          value={getEditedValue(product.id, 'minimum_level', product.minimum_level || 0)}
                          onChange={(e) => handleFieldChange(product.id, 'minimum_level', e.target.value)}
                          data-testid={`min-input-${product.id}`}
                        />
                      </TableCell>
                      <TableCell className="bg-yellow-50/50">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          className="w-full text-center h-8"
                          value={getEditedValue(product.id, 'reorder_level', product.reorder_level || 0)}
                          onChange={(e) => handleFieldChange(product.id, 'reorder_level', e.target.value)}
                          data-testid={`reorder-input-${product.id}`}
                        />
                      </TableCell>
                      <TableCell className="bg-green-50/50">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          className="w-full text-center h-8"
                          value={getEditedValue(product.id, 'maximum_level', product.maximum_level || 0)}
                          onChange={(e) => handleFieldChange(product.id, 'maximum_level', e.target.value)}
                          data-testid={`max-input-${product.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        {isEdited(product.id) && (
                          <span className="text-orange-500 text-xs">*</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center">
          <Button 
            variant="outline" 
            onClick={loadMore}
            disabled={loading}
            data-testid="load-more-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Load More Products
          </Button>
        </div>
      )}

      {/* Sticky Save Bar when there are edits */}
      {editedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-orange-600">{editedCount} products</span> have unsaved changes
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleDiscardChanges}
              >
                Discard Changes
              </Button>
              <Button 
                onClick={handleSaveAll}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save All Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockLevelBulkEditor;
